import {useGenerateBrandVoice} from "hooks/open-ai-api/useGenerateBrandVoice";
import {useMemo, useState} from "react";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {ref, set} from "firebase/database";
import {database} from "hooks/firebase";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {useActivePurchasedProduct} from "data/access/purchase/useActivePurchasedProduct";
import {BrandVoiceResult} from "data/models/deliverables/brand-styles";
import {useObjectVal} from "react-firebase-hooks/database";
import * as Sentry from "@sentry/browser";


type BrandVoiceType = {
  brandVoice: BrandVoiceResult | null;
  availableCreditsToUse: number;
  canGenerateNew: boolean;
}


export function useBrandVoice(): [
  () => Promise<void>,
  BrandVoiceType,
  boolean,
      Error | null
] {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const bandVoiceDeliverablePath = useMemo(() => `user-data/${user?.uid ?? ""}/deliverables/brand-voice/result`, [user]);
  const [savedBrandVoice, savedBrandVoiceLoading, savedBrandVoiceError] =
      useObjectVal<BrandVoiceResult>((user && ref(database, bandVoiceDeliverablePath)) ?? null);

  const [purchasedProduct, purchasedProductLoading, purchasedProductError] = useActivePurchasedProduct('bc-express-annual');

  const {generateBrandVoice, generatePrompt, readyToGenerateBrandVoice} = useGenerateBrandVoice();
  const [generating, setGenerating] = useState<boolean>(false);

  const anyLoading = (savedBrandVoiceLoading || purchasedProductLoading);

  const anError = (savedBrandVoiceError ?? purchasedProductError ?? null);

  const newPrompt = useMemo(() => generatePrompt(savedBrandVoice?.maxGuidelinesLength ?? 500), [savedBrandVoice?.maxGuidelinesLength, generatePrompt]);

  const generateVoiceFlag = useMemo( () => (
      readyToGenerateBrandVoice
      && !anyLoading
      && anError === null
      && !generating
      && !isNullOrUndefined(user)
  ), [
    readyToGenerateBrandVoice,
    anyLoading,
    anError,
    generating,
    user,
  ]);

  const canGenerateNew = useMemo(() => generateVoiceFlag && (newPrompt ?? "") != (savedBrandVoice?.prompt ?? ""),
      [generateVoiceFlag, newPrompt, savedBrandVoice?.prompt]);

  const generateAndSaveBrandVoice = async () => {
    setGenerating(true);
    await generateBrandVoice()
        .then((result) => set(ref(database, bandVoiceDeliverablePath), result))
        .then(() => setGenerating(false))
        .catch(error => Sentry.captureException(error));
  }

  return [
    generateAndSaveBrandVoice,
    {
    brandVoice: savedBrandVoice ?? null,
    availableCreditsToUse: purchasedProduct !== null ? 1 : 0,
    canGenerateNew,
  }, anyLoading || generating, anError];
}
